import { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  RefreshCw,
  AlertTriangle,
  Clock,
  Package,
  Calendar,
  Download,
  FileText,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  useOpsReport,
  useExportGearRequestsCSV,
} from '@/hooks/use-gear-request-templates';
import { useOrgTimeZone, useOrgToday } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { GearRequest, GearRequestStatus, GearRequestPriority } from '@/lib/types';

const statusColors: Record<GearRequestStatus, string> = {
  pending_confirmation: 'bg-amber-100 text-amber-800',
  requested: 'bg-yellow-100 text-yellow-800',
  fulfilled: 'bg-blue-100 text-blue-800',
  in_transit: 'bg-purple-100 text-purple-800',
  partially_received: 'bg-orange-100 text-orange-800',
  received: 'bg-green-100 text-green-800',
  returned: 'bg-teal-100 text-teal-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

const statusLabels: Record<GearRequestStatus, string> = {
  pending_confirmation: 'Pending Confirmation',
  requested: 'Requested',
  fulfilled: 'Fulfilled',
  in_transit: 'In Transit',
  partially_received: 'Partially Received',
  received: 'Received',
  returned: 'Returned to Inventory',
  cancelled: 'Cancelled',
};

const priorityColors: Record<GearRequestPriority, string> = {
  low: 'bg-gray-100 text-gray-700',
  normal: 'bg-blue-50 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

export function GearRequestOpsReportPage() {
  const orgTimeZone = useOrgTimeZone();
  const orgToday = useOrgToday();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || 'all';

  const { data, isLoading, isError, refetch } = useOpsReport({ category });
  const exportCSV = useExportGearRequestsCSV();

  const requests = data?.results || [];
  const summary = data?.summary || { total: 0, overdue: 0, in_transit_too_long: 0, partial: 0 };

  const setCategory = useCallback(
    (value: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (value === 'all') {
        newParams.delete('category');
      } else {
        newParams.set('category', value);
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Ops Report
          </h1>
          <p className="text-gray-600 mt-1">
            Stuck, overdue, and partially received gear requests
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => exportCSV.mutate()}
            disabled={exportCSV.isPending}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Open Requests"
          value={summary.total}
          icon={<Package className="h-5 w-5 text-blue-600" />}
          isActive={category === 'all'}
          onClick={() => setCategory('all')}
        />
        <SummaryCard
          title="Overdue"
          value={summary.overdue}
          icon={<AlertTriangle className="h-5 w-5 text-red-600" />}
          isActive={category === 'overdue'}
          onClick={() => setCategory('overdue')}
          variant="destructive"
        />
        <SummaryCard
          title="In Transit Too Long"
          value={summary.in_transit_too_long}
          icon={<Clock className="h-5 w-5 text-amber-600" />}
          isActive={category === 'in_transit_too_long'}
          onClick={() => setCategory('in_transit_too_long')}
        />
        <SummaryCard
          title="Partial Receipt"
          value={summary.partial}
          icon={<FileText className="h-5 w-5 text-orange-600" />}
          isActive={category === 'partial'}
          onClick={() => setCategory('partial')}
        />
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Select
              value={category}
              onValueChange={setCategory}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Open</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="in_transit_too_long">In Transit Too Long</SelectItem>
                <SelectItem value="partial">Partial Receipt</SelectItem>
              </SelectContent>
            </Select>
            <Link to="/gear-requests">
              <Button variant="link" className="text-sm">
                View all requests
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Results table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-red-600">
              Failed to load ops report. Please try again.
            </div>
          ) : requests.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No stuck or overdue requests found.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Requester</TableHead>
                  <TableHead>Lines</TableHead>
                  <TableHead>Needed By</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((req) => (
                  <OpsRequestRow
                    key={req.id}
                    request={req}
                    timeZone={orgTimeZone}
                    orgToday={orgToday}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryCard({
  title,
  value,
  icon,
  isActive,
  onClick,
  variant,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
  variant?: string;
}) {
  return (
    <Card
      className={`cursor-pointer transition-all hover:shadow-md ${
        isActive ? 'ring-2 ring-blue-500' : ''
      }`}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-600">
          {title}
        </CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div
          className={`text-2xl font-bold ${
            variant === 'destructive' && value > 0 ? 'text-red-600' : ''
          }`}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function OpsRequestRow({
  request,
  timeZone,
  orgToday,
}: {
  request: GearRequest;
  timeZone: string;
  orgToday: string;
}) {
  const overdue =
    request.needed_by &&
    request.needed_by.slice(0, 10) < orgToday &&
    request.status !== 'received' &&
    request.status !== 'cancelled';

  return (
    <TableRow>
      <TableCell>
        <Link
          to={`/gear-requests/${request.id}`}
          className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
        >
          #{request.id}
        </Link>
      </TableCell>
      <TableCell>
        <Badge className={statusColors[request.status]}>
          {statusLabels[request.status]}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge className={priorityColors[request.priority]}>
          {request.priority}
        </Badge>
      </TableCell>
      <TableCell>{request.requested_by_username || '\u2014'}</TableCell>
      <TableCell>{request.lines.length}</TableCell>
      <TableCell>
        {request.needed_by ? (
          <div
            className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : ''}`}
          >
            <Calendar className="h-4 w-4" />
            <span>{formatDateOnly(request.needed_by)}</span>
            {overdue && (
              <Badge variant="destructive" className="ml-1">
                Overdue
              </Badge>
            )}
          </div>
        ) : (
          <span className="text-gray-400">{'\u2014'}</span>
        )}
      </TableCell>
      <TableCell className="text-gray-500">
        {formatDateTimeInTimeZone(request.created_at, timeZone)}
      </TableCell>
    </TableRow>
  );
}
